import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';

/**
 * Formats a whole-second duration as:
 * - mm:ss when under 1 hour (e.g. 42:15)
 * - h:mm:ss when crossing 1 hour (e.g. 1:15:30)
 */
export const formatElapsed = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Formats duration in minutes for completed trips or summary cards:
 * - Under 1 hour: "45 mins"
 * - 1 hour or above: "1h 15m (75 mins)" / "2 hrs (120 mins)"
 */
export const formatDuration = (minutes) => {
  if (minutes == null || isNaN(minutes)) return '—';
  const m = Math.round(Number(minutes));
  if (m < 60) return `${m} mins`;
  const hours = Math.floor(m / 60);
  const remainingMins = m % 60;
  if (remainingMins === 0) return `${hours} hr${hours > 1 ? 's' : ''} (${m} mins)`;
  return `${hours}h ${remainingMins}m (${m} mins)`;
};

/**
 * SLA Timer Component:
 * - Normal (< 45 min): green/emerald badge
 * - Amber warning (45 min - 60 min): amber badge
 * - Red critical / breached (>= 60 min): red badge
 * - Shows mm:ss when < 1hr, and h:mm:ss when >= 1hr
 */
const SlaTimer = ({
  startAt,
  warnAmberSeconds = 45 * 60,
  warnRedSeconds = 60 * 60,
  warnAfterSeconds, // Backwards compatibility for single-threshold callers
  showLabel = false,
  label = 'Trip SLA',
  className = '',
}) => {
  const [now, setNow] = useState(() => Date.now());
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: false });
  const badgeRef = useRef(null);

  useEffect(() => {
    if (!startAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startAt]);

  const updatePosition = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setIsHovered(false);
      return;
    }
    const placeAbove = window.innerHeight - rect.bottom < 55;
    const maxLeft = typeof window !== 'undefined' ? Math.max(8, window.innerWidth - 340) : 8;
    const left = Math.min(Math.max(8, rect.left), maxLeft);
    setCoords({
      top: placeAbove ? rect.top - 6 : rect.bottom + 6,
      left,
      placeAbove,
    });
  };

  useEffect(() => {
    if (!isHovered) return undefined;
    const handleScrollOrResize = () => {
      updatePosition();
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isHovered]);

  if (!startAt) return null;
  const startMs = new Date(startAt).getTime();
  if (!isFinite(startMs)) return null;

  const elapsedSeconds = Math.max(0, Math.floor((now - startMs) / 1000));

  // Determine SLA status
  const effectiveRedThreshold = warnAfterSeconds != null ? warnAfterSeconds : warnRedSeconds;
  const isRed = effectiveRedThreshold != null && elapsedSeconds >= effectiveRedThreshold;
  const isAmber = !isRed && warnAmberSeconds != null && elapsedSeconds >= warnAmberSeconds;

  const amberMin = warnAmberSeconds ? Math.round(warnAmberSeconds / 60) : null;
  const redMin = effectiveRedThreshold ? Math.round(effectiveRedThreshold / 60) : null;

  let badgeColor = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  let statusText = amberMin ? `Normal (< ${amberMin}m)` : 'Normal';
  if (isRed) {
    badgeColor = 'bg-red-50 text-red-600 border border-red-200 font-semibold';
    statusText = redMin ? `Breached (>= ${redMin}m)` : 'Breached';
  } else if (isAmber) {
    badgeColor = 'bg-amber-50 text-amber-700 border border-amber-300 font-medium';
    statusText = amberMin && redMin ? `Warning (${amberMin}m - ${redMin}m)` : 'Warning';
  }

  const tooltipText = `${label}: ${formatElapsed(elapsedSeconds)} (${statusText}) — started at ${new Date(startMs).toLocaleTimeString()}`;

  return (
    <>
      <span
        ref={badgeRef}
        onMouseEnter={() => {
          updatePosition();
          setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs tabular-nums cursor-default select-none transition-colors ${badgeColor} ${className}`}
      >
        <Clock className="w-3 h-3 shrink-0" />
        {showLabel && <span className="font-normal opacity-80">SLA:</span>}
        {formatElapsed(elapsedSeconds)}
      </span>

      {isHovered && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            transform: coords.placeAbove ? 'translateY(-100%)' : 'none',
            zIndex: 99999,
          }}
          className="pointer-events-none whitespace-nowrap bg-gray-900 text-white text-[11px] leading-tight font-normal px-2.5 py-1.5 rounded shadow-xl border border-gray-800"
        >
          {tooltipText}
        </div>,
        document.body
      )}
    </>
  );
};

/**
 * Resolves the trip start timestamp for Total Trip SLA tracking.
 * Loophole handled: for SCHEDULED bookings, timer does not start when driver accepts in advance;
 * it only begins once scheduled time arrives (or when driver actively starts moving).
 */
export const getTripStartTime = (trip) => {
  if (!trip) return null;
  const isScheduled = trip.bookingType === 'SCHEDULED' || trip.when?.label === 'Scheduled';
  const scheduledTime = trip.scheduledDatetime || (isScheduled ? trip.when?.at : null);

  if (isScheduled && scheduledTime) {
    const scheduledMs = new Date(scheduledTime).getTime();
    const activeStages = ['Active Trips', 'Towards Pickup', 'At Pickup', 'Towards Drop', 'At Drop', 'Untracked', 'Completed'];
    if (!activeStages.includes(trip.stage) && Date.now() < scheduledMs) {
      return null;
    }
    return scheduledTime;
  }

  return trip.towardsPickupStart || trip.trackingStartTime || trip.createdAt || trip.when?.at || null;
};

/**
 * Returns active stage timer config if current stage has an SLA timer:
 * - Towards Pickup: starts when driver accepts/starts moving towards pickup
 * - At Pickup: wait time at pickup, starts from arrivedAt
 * - Towards Drop: travel time to hospital/dropoff, starts from departedPickupAt (or arrivedAt)
 * - At Drop: wait time at drop-off, starts from atDropoffAt
 */
export const getActiveStageTimerConfig = (trip) => {
  if (!trip) return null;
  switch (trip.stage) {
    case 'Towards Pickup': {
      const startAt = trip.towardsPickupStart || getTripStartTime(trip);
      if (!startAt) return null;
      return {
        stage: 'Towards Pickup',
        label: 'Stage:',
        startAt,
        warnAmberSeconds: 20 * 60, // 20 mins amber warning
        warnRedSeconds: 30 * 60,   // 30 mins red critical
      };
    }
    case 'At Pickup': {
      if (!trip.arrivedAt) return null;
      return {
        stage: 'At Pickup',
        label: 'Stage:',
        startAt: trip.arrivedAt,
        warnAmberSeconds: 15 * 60, // 15 mins amber warning
        warnRedSeconds: 30 * 60,   // 30 mins red critical
      };
    }
    case 'Towards Drop': {
      const startAt = trip.departedPickupAt || trip.arrivedAt || trip.updatedAt;
      if (!startAt) return null;
      return {
        stage: 'Towards Drop',
        label: 'Stage:',
        startAt,
        warnAmberSeconds: 30 * 60, // 30 mins amber warning
        warnRedSeconds: 45 * 60,   // 45 mins red critical
      };
    }
    case 'At Drop': {
      if (!trip.atDropoffAt) return null;
      return {
        stage: 'At Drop',
        label: 'Stage:',
        startAt: trip.atDropoffAt,
        warnAmberSeconds: 15 * 60, // 15 mins amber warning
        warnRedSeconds: 30 * 60,   // 30 mins red critical
      };
    }
    default:
      return null;
  }
};

/**
 * Returns a complete breakdown of each leg of the trip with either live running timer or fixed duration.
 */
export const getTripLegBreakdown = (trip) => {
  if (!trip) return [];
  const legs = [];

  const tripStart = trip.towardsPickupStart || getTripStartTime(trip);
  const arrivedAt = trip.arrivedAt;
  const departedPickupAt = trip.departedPickupAt || (arrivedAt && !['Towards Pickup', 'At Pickup'].includes(trip.stage) ? arrivedAt : null);
  const atDropoffAt = trip.atDropoffAt;
  const completedAt = trip.completedAt || (trip.stage === 'Completed' ? trip.updatedAt : null);

  // Leg 1: Towards Pickup
  if (trip.stage === 'Towards Pickup') {
    legs.push({ label: 'Towards Pickup', isLive: true, startAt: tripStart, warnAmberSeconds: 20 * 60, warnRedSeconds: 30 * 60 });
  } else if (arrivedAt && tripStart) {
    const mins = Math.max(1, Math.round((new Date(arrivedAt).getTime() - new Date(tripStart).getTime()) / 60000));
    legs.push({ label: 'Towards Pickup', isLive: false, minutes: mins });
  }

  // Leg 2: Wait at Pickup
  if (trip.stage === 'At Pickup') {
    legs.push({ label: 'Wait at Pickup', isLive: true, startAt: arrivedAt, warnAmberSeconds: 15 * 60, warnRedSeconds: 30 * 60 });
  } else if (arrivedAt && departedPickupAt && new Date(departedPickupAt).getTime() > new Date(arrivedAt).getTime()) {
    const mins = Math.max(1, Math.round((new Date(departedPickupAt).getTime() - new Date(arrivedAt).getTime()) / 60000));
    legs.push({ label: 'Wait at Pickup', isLive: false, minutes: mins });
  }

  // Leg 3: Towards Drop
  if (trip.stage === 'Towards Drop') {
    legs.push({ label: 'Towards Drop', isLive: true, startAt: departedPickupAt || arrivedAt, warnAmberSeconds: 30 * 60, warnRedSeconds: 45 * 60 });
  } else if (atDropoffAt && (departedPickupAt || arrivedAt)) {
    const fromTime = departedPickupAt || arrivedAt;
    const mins = Math.max(1, Math.round((new Date(atDropoffAt).getTime() - new Date(fromTime).getTime()) / 60000));
    legs.push({ label: 'Towards Drop', isLive: false, minutes: mins });
  }

  // Leg 4: Wait at Drop
  if (trip.stage === 'At Drop') {
    legs.push({ label: 'Wait at Drop', isLive: true, startAt: atDropoffAt, warnAmberSeconds: 15 * 60, warnRedSeconds: 30 * 60 });
  } else if (completedAt && atDropoffAt && new Date(completedAt).getTime() > new Date(atDropoffAt).getTime()) {
    const mins = Math.max(1, Math.round((new Date(completedAt).getTime() - new Date(atDropoffAt).getTime()) / 60000));
    legs.push({ label: 'Wait at Drop', isLive: false, minutes: mins });
  }

  return legs;
};

export default SlaTimer;
